// Play Selections will be logged by Oscar standing next to the play caller:
// 1. Personnel Group yelled from sideline
// 2. Formation (and direction) signaled from sideline; can be repeated to Oscar
// 3. For run plays the code word and any tags are yelled from the sideline
// 4. For pass plays the pass protection concept and pass concept are yelled from the sideline

import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  SelectChangeEvent,
} from "@mui/material";
import { useSchemes } from "../../../../contexts/SchemesContext";
import { useLogger } from "../../../../contexts/LoggerContext";
import { OffensivePlaySelection } from "../../../../types/LogTypes";

export const OffensePlaySelection = () => {
  const { clearSelections, setCurrentPlayCall } = useLogger();
  const {
    formations,
    runConcepts,
    passProtectionConcepts,
    passConcepts,
    offensivePersonnel,
    formationTags,
    offensivePlayTags,
  } = useSchemes();

  // TODO: create a filter to only include possible run concepts based on the formation
  // direction

  // Create a structure that maintains the relationship between code words and their concept
  const allRunCodeWords = runConcepts.flatMap((concept) => [
    {
      conceptId: concept.id,
      codeWord: concept.code_word_left,
      // Create a unique value that encodes both the concept ID and which side it is
      value: `${concept.id}-left`
    },
    {
      conceptId: concept.id,
      codeWord: concept.code_word_right,
      value: `${concept.id}-right`
    }
  ]);

  const [selectedPersonnel, setSelectedPersonnel] = useState<number | null>(
    null
  );
  const [selectedFormation, setSelectedFormation] = useState<number | null>(
    null
  );
  // Store the selected value which contains both concept ID and side
  const [selectedRunValue, setSelectedRunValue] = useState<string | null>(null);
  const [selectedPassProtectionConcept, setSelectedPassProtectionConcept] =
    useState<number | null>(null);
  const [selectedFormationTags, setSelectedFormationTags] = useState<number[]>(
    []
  );
  const [selectedPassConcept, setSelectedPassConcept] = useState<number | null>(
    null
  );

  const [selectedPlayTags, setSelectedPlayTags] = useState<number | null>(null);

  const [formationDirection, setFormationDirection] = useState<
    "Left" | "Right"
  >("Right");

  useEffect(() => {
    // Get the selected run concept details if one is selected
    const selectedRunDetails = selectedRunValue 
      ? allRunCodeWords.find(cw => cw.value === selectedRunValue)
      : null;

    const playCall: OffensivePlaySelection = {
      personnel_group_id: selectedPersonnel,
      formation_id: selectedFormation,
      run_concept_id: selectedRunDetails?.conceptId || null,
      direction: formationDirection,
      formation_tags: selectedFormationTags,
      play_tag_id: selectedPlayTags,
      pass_protection_concept_id: selectedPassProtectionConcept,
      pass_concept_id: selectedPassConcept,
      run_code_word: selectedRunDetails?.codeWord || null,
    };
    setCurrentPlayCall(playCall);
  }, [
    selectedPersonnel,
    selectedFormation,
    selectedRunValue,
    formationDirection,
    selectedFormationTags,
    selectedPlayTags,
    selectedPassProtectionConcept,
    selectedPassConcept,
    setCurrentPlayCall,
  ]);

  useEffect(() => {
    if (clearSelections) {
      setSelectedPersonnel(null);
      setSelectedFormation(null);
      setSelectedRunValue(null);
      setSelectedFormationTags([]);
      setSelectedPlayTags(null);
      setSelectedPassProtectionConcept(null);
      setSelectedPassConcept(null);
    }
  }, [clearSelections]);

  const handlePersonnelChange = (event: SelectChangeEvent<number | "">) => {
    setSelectedPersonnel(event.target.value as number);
  };

  const handleFormationChange = (event: SelectChangeEvent<number | "">) => {
    setSelectedFormation(event.target.value as number);
  };

  const handleFormationTagsChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value;
    setSelectedFormationTags(
      typeof value === "string"
        ? value.split(",").map(Number)
        : (value as number[])
    );
  };

  const handleDirectionChange = (
    event: SelectChangeEvent<"Left" | "Right">
  ) => {
    setFormationDirection(event.target.value as "Left" | "Right");
  };

  const handleRunConceptChange = (event: SelectChangeEvent<string | "">) => {
    const value = event.target.value as string;
    setSelectedRunValue(value || null);
  };

  const handlePlayTagsChange = (event: SelectChangeEvent<number>) => {
    setSelectedPlayTags(event.target.value as number);
  };

  const handlePassProtectionConceptChange = (
    event: SelectChangeEvent<number | "">
  ) => {
    setSelectedPassProtectionConcept(event.target.value as number);
  };

  const handlePassConceptChange = (event: SelectChangeEvent<number | "">) => {
    setSelectedPassConcept(event.target.value as number);
  };

  return (
    <Paper elevation={3} sx={{ p: 1 }}>
      <Box>
        <Grid container spacing={2}>
          {/* Personnel Group Selection */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel id="personnel-group-label">
                Personnel Group
              </InputLabel>
              <Select
                labelId="personnel-group-label"
                id="personnel-group"
                value={selectedPersonnel || ""}
                label="Personnel Group"
                onChange={handlePersonnelChange}
              >
                {offensivePersonnel.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Formation Selection */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel id="formation-label">Formation</InputLabel>
              <Select
                labelId="formation-label"
                id="formation"
                value={selectedFormation || ""}
                label="Formation"
                onChange={handleFormationChange}
              >
                {formations.map((formation) => (
                  <MenuItem key={formation.id} value={formation.id}>
                    {formation.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Formation Direction */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel id="formation-direction-label">
                Formation Direction
              </InputLabel>
              <Select
                labelId="formation-direction-label"
                id="formation-direction"
                value={formationDirection}
                label="Formation Direction"
                onChange={handleDirectionChange}
              >
                <MenuItem value="Left">Left</MenuItem>
                <MenuItem value="Right">Right</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Formation Tags */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel id="formation-tags-label">Formation Tags</InputLabel>
              <Select
                labelId="formation-tags-label"
                id="formation-tags"
                value={selectedFormationTags}
                label="Formation Tags"
                onChange={handleFormationTagsChange}
                multiple
              >
                {formationTags.map((formation) => (
                  <MenuItem key={formation.id} value={formation.id}>
                    {formation.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Run Concept Selection */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel id="run-concept-label">Run Concept</InputLabel>
              <Select
                labelId="run-concept-label"
                id="run-concept"
                value={selectedRunValue || ""}
                label="Run Concept"
                onChange={handleRunConceptChange}
              >
                {allRunCodeWords.map((codeWord) => (
                  <MenuItem key={codeWord.value} value={codeWord.value}>
                    {codeWord.codeWord}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/*  Pass Protection Concept Selection */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel id="pass-pro-label">Pass Pro</InputLabel>
              <Select
                labelId="pass-pro-label"
                id="pass-pro"
                value={selectedPassProtectionConcept || ""}
                label="Pass Protection Concept"
                onChange={handlePassProtectionConceptChange}
              >
                {passProtectionConcepts.map((passProtectionConcept) => (
                  <MenuItem
                    key={passProtectionConcept.id}
                    value={passProtectionConcept.id}
                  >
                    {passProtectionConcept.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Pass Concept Selection */}

          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel id="pass-concept-label">Pass Concept</InputLabel>
              <Select
                labelId="pass-concept-label"
                id="pass-concept"
                value={selectedPassConcept || ""}
                label="Pass Concept"
                onChange={handlePassConceptChange}
              >
                {passConcepts.map((passConcept) => (
                  <MenuItem key={passConcept.id} value={passConcept.id}>
                    {passConcept.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Play Tag */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel id="play-tags-label">Play Tag</InputLabel>
              <Select
                labelId="play-tag-label"
                id="play-tag"
                value={selectedPlayTags || ""}
                label="Play Tag"
                onChange={handlePlayTagsChange}
              >
                {offensivePlayTags.map((playTag) => (
                  <MenuItem key={playTag.id} value={playTag.id}>
                    {playTag.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};
